// ── BCHNostr — Podcast seeding ────────────────────────────────────
//
// A one-time operator tool to populate the BCH Podcasts directory with
// real, existing shows. Each seeded show is published under its OWN freshly
// generated Nostr identity — NOT the operator's key — because a show is an
// independent publisher and 20 shows all authored by one pubkey would be
// dishonest (and would collide on the one-show-per-key model).
//
// For each pasted URL we:
//   1. discover + validate the RSS feed (lib/podcastResolver),
//   2. generate a dedicated keypair,
//   3. publish kind 0 (profile identity/avatar so the show has a face) and
//      kind 30078 (the show event with the feed + metadata),
//   4. return the generated nsec so the operator can save/re-run it.
//
// Nothing here touches the logged-in user's signer: events are signed
// locally with finalizeEvent. Audio is never fetched, hosted, or proxied —
// only the small feed XML is read, exactly as the normal setup flow does.

import { generateSecretKey, getPublicKey, finalizeEvent, nip19 } from 'nostr-tools';
import type { Event } from 'nostr-tools';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { resolvePodcast, type PodcastPlatform } from './podcastResolver';
import {
  PODCAST_KIND, buildPodcastEvent, refreshFeed, safeFeedUrl,
} from './podcast';
import { getRelays } from '../hooks/useNostrPublish';

// ── Deterministic key derivation ──────────────────────────────────
//
// Random per-show keys are lost the moment you close the results screen, so
// maintaining a seeded show meant carefully hoarding an nsec file. Instead we
// derive each show's key DETERMINISTICALLY from three things:
//
//   passphrase   a secret only the operator knows (never stored)
//   operator     the operator's pubkey, as a salt (namespaces the whole set)
//   feedUrl      the show's own RSS feed URL
//
// The same passphrase + feed URL ALWAYS yields the same identity, from any
// browser, with nothing to save: to update a show you just paste its URL and
// re-enter the passphrase. The passphrase is the only secret — treat it like
// a master key. A different passphrase produces an entirely different, unre-
// lated set of shows, so a leaked/rotated passphrase can't be linked back.
//
// HKDF-SHA256 expands the passphrase over the feed URL; the 32-byte output is
// reduced mod n into a valid secp256k1 scalar (rejecting the ~2^-128 chance
// of zero). This is standard, audited noble crypto — no home-rolled hashing.

/** secp256k1 group order. */
const SECP256K1_N = BigInt(
  '0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141',
);

const DERIVE_PREFIX = 'bchnostr-podcast-seed:v1:';

function bytesToBigInt(bytes: Uint8Array): bigint {
  let n = BigInt(0);
  for (const b of bytes) n = (n << BigInt(8)) | BigInt(b);
  return n;
}

function bigIntTo32Bytes(n: bigint): Uint8Array {
  const out = new Uint8Array(32);
  for (let i = 31; i >= 0; i--) {
    out[i] = Number(n & BigInt(0xff));
    n >>= BigInt(8);
  }
  return out;
}

/**
 * Derive a stable secp256k1 secret key for one show. Deterministic in
 * (passphrase, operatorPubkey, feedUrl); infeasible to reverse to the
 * passphrase. Returns null only for the negligible zero-scalar case.
 */
export function deriveShowKey(
  passphrase: string,
  operatorPubkey: string,
  feedUrl: string,
): Uint8Array | null {
  const enc = new TextEncoder();
  const ikm = enc.encode(passphrase);
  const salt = enc.encode(DERIVE_PREFIX + operatorPubkey.toLowerCase());
  const info = enc.encode(feedUrl);
  // 32 bytes of key material bound to this exact (passphrase, operator, feed).
  const okm = hkdf(sha256, ikm, salt, info, 32);
  const scalar = bytesToBigInt(okm) % SECP256K1_N;
  if (scalar === BigInt(0)) return null;
  return bigIntTo32Bytes(scalar);
}

// ── Result shape ──────────────────────────────────────────────────

export type SeedStatus = 'ok' | 'failed';

export interface SeedResult {
  /** The URL the operator pasted. */
  input: string;
  status: SeedStatus;
  /** Populated on success. */
  name: string;
  feedUrl: string;
  episodes: number;
  platform: PodcastPlatform | null;
  /** The generated identity, so the operator can save / reuse it. */
  npub: string;
  nsec: string;
  pubkey: string;
  /** True when an existing identity (a supplied nsec) was reused = an update. */
  updated: boolean;
  /** True when the key was derived deterministically from the passphrase. */
  deterministic: boolean;
  /** How many relays accepted the show event. */
  acceptedRelays: number;
  /** Populated on failure. */
  error: string;
}

export interface SeedProgress {
  index: number;
  total: number;
  input: string;
  phase: 'resolving' | 'publishing' | 'done';
}

// ── Direct relay publish (operator-key signing) ───────────────────
//
// The normal useNostrPublish() path signs with window.nostr — the logged-in
// user. Seeding signs each event with the SHOW's own generated key, so we
// publish straight to the relays here. Mirrors the accept/timeout handling
// of useNostrPublish, minus the local-cache and client-tag machinery (a
// seeded show is not the operator's own content).

const PUBLISH_TIMEOUT_MS = 10000;

function publishSigned(signed: Event, relays: string[]): Promise<number> {
  const attempts = relays.map(url => new Promise<boolean>((resolve) => {
    let finished = false;
    const done = (accepted: boolean, ws?: WebSocket) => {
      if (finished) return;
      finished = true;
      try { ws?.close(); } catch {}
      resolve(accepted);
    };
    try {
      const ws = new WebSocket(url);
      ws.onopen = () => {
        try { ws.send(JSON.stringify(['EVENT', signed])); }
        catch { done(false, ws); }
      };
      ws.onmessage = (msgEv) => {
        try {
          const msg = JSON.parse(msgEv.data);
          if (Array.isArray(msg) && msg[0] === 'OK' && msg[1] === signed.id) {
            done(msg[2] === true, ws);
          }
        } catch {}
      };
      ws.onerror = () => done(false, ws);
      ws.onclose = () => done(false);
      setTimeout(() => done(false, ws), PUBLISH_TIMEOUT_MS);
    } catch {
      resolve(false);
    }
  }));

  return Promise.allSettled(attempts).then(settled =>
    settled.filter(s => s.status === 'fulfilled' && s.value).length,
  );
}

// ── Input parsing ─────────────────────────────────────────────────

/** One line of seed input: a feed URL and an optional identity to reuse. */
export interface SeedInput {
  url: string;
  /** When present, the show is published under THIS key (an update). */
  nsec?: string;
}

const NSEC_RE = /\bnsec1[0-9a-z]{20,}\b/i;
const URL_RE = /\bhttps?:\/\/[^\s,]+/i;

/**
 * Parse a textarea blob into seed entries. Each non-comment line may contain
 * a URL and, optionally, an `nsec1…` to reuse — so re-running the seeder
 * UPDATES an existing show rather than creating a duplicate. Order and
 * separators are flexible ("url, nsec", "url nsec", "nsec url"), which means
 * the tool's own downloaded key backup can be pasted straight back in.
 *
 * A bare hostname (no scheme) is still accepted as a URL when the line has no
 * explicit http(s):// match, mirroring the loose URL handling elsewhere.
 */
export function parseSeedInput(raw: string): SeedInput[] {
  const out: SeedInput[] = [];
  for (const rawLine of raw.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;

    const nsecMatch = line.match(NSEC_RE);
    const nsec = nsecMatch ? nsecMatch[0] : undefined;

    // Strip the nsec out before hunting for the URL so they never overlap.
    const withoutNsec = nsec ? line.replace(nsec, ' ').trim() : line;

    let url = '';
    const urlMatch = withoutNsec.match(URL_RE);
    if (urlMatch) {
      url = urlMatch[0].replace(/[),.]+$/, '');
    } else {
      // No explicit scheme — take the first non-nsec token as a bare host.
      const token = withoutNsec.split(/[\s,]+/).find(t => t && !NSEC_RE.test(t));
      if (token) url = token;
    }

    if (!url) continue;
    out.push({ url, nsec });
  }
  return out;
}

// ── Category guessing ─────────────────────────────────────────────
//
// The show event needs a category id. We can't ask the operator per-show
// without turning this into a chore, so we make a cheap keyword guess from
// the title/description and default to 'bitcoin' (this is a BCH directory).

function guessCategory(title: string, description: string): string {
  const text = `${title} ${description}`.toLowerCase();
  const rules: Array<[RegExp, string]> = [
    [/\b(bitcoin|bch|crypto|satoshi|lightning|blockchain|monero)\b/, 'bitcoin'],
    [/\b(tech|software|developer|programming|engineering|ai|startup)\b/, 'technology'],
    [/\b(business|entrepreneur|finance|market|economy|invest)\b/, 'business'],
    [/\b(news|politics|current affairs|geopolitic)\b/, 'news'],
    [/\b(learn|education|teach|course|history|science)\b/, 'education'],
    [/\b(comedy|funny|humor|humour)\b/, 'comedy'],
    [/\b(health|fitness|wellness|medita|nutrition)\b/, 'health'],
    [/\b(music|band|album|song)\b/, 'music'],
    [/\b(sport|football|soccer|basketball|mma|boxing)\b/, 'sports'],
    [/\b(culture|society|philosophy|art|interview)\b/, 'culture'],
  ];
  for (const [re, id] of rules) if (re.test(text)) return id;
  return 'bitcoin';
}

// ── Seed one show ─────────────────────────────────────────────────

/**
 * Resolve, sign and publish a single podcast under a fresh identity.
 * Optionally reuse a provided nsec so re-running the seeder updates the SAME
 * show identity instead of creating a duplicate.
 */
export async function seedPodcast(
  entry: SeedInput,
  opts: {
    onPhase?: (phase: SeedProgress['phase']) => void;
    /** Deterministic-key mode: derive from this passphrase + operator pubkey. */
    passphrase?: string;
    operatorPubkey?: string;
  } = {},
): Promise<SeedResult> {
  const input = entry.url;
  const base: SeedResult = {
    input, status: 'failed', name: '', feedUrl: '', episodes: 0, platform: null,
    npub: '', nsec: '', pubkey: '', updated: false, deterministic: false,
    acceptedRelays: 0, error: '',
  };

  const reusing = !!entry.nsec;
  const usePassphrase = !entry.nsec && !!opts.passphrase && !!opts.operatorPubkey;

  // An explicit nsec on the line is decoded up front so a bad key fails fast,
  // before any network call. (Derived keys are computed AFTER resolving,
  // since the derivation is bound to the show's resolved feed URL.)
  let explicitKey: Uint8Array | null = null;
  if (entry.nsec) {
    try {
      const decoded = nip19.decode(entry.nsec.trim());
      if (decoded.type !== 'nsec') throw new Error('bad nsec');
      explicitKey = decoded.data as Uint8Array;
    } catch {
      return { ...base, updated: true, error: 'The provided nsec is invalid.' };
    }
  }

  // 1. Resolve + validate the feed.
  opts.onPhase?.('resolving');
  const resolved = await resolvePodcast(input);
  if (!resolved.ok || !resolved.feed) {
    return { ...base, updated: reusing, deterministic: usePassphrase, error: resolved.message || 'Could not resolve a valid feed.' };
  }

  const { feed, feedUrl, publisher, platform } = resolved;

  // 2. Resolve the identity, now that we have the canonical feed URL.
  //    Priority: explicit nsec  →  deterministic derivation  →  random.
  let secretKey: Uint8Array;
  if (explicitKey) {
    secretKey = explicitKey;
  } else if (usePassphrase) {
    // Derive from the CANONICAL feed URL (safeFeedUrl-normalised) so the same
    // show always maps to the same key regardless of which directory link was
    // pasted (Apple page one day, raw RSS the next both resolve to this URL).
    const canonical = safeFeedUrl(feedUrl) || feedUrl;
    const derived = deriveShowKey(opts.passphrase!, opts.operatorPubkey!, canonical);
    if (!derived) {
      return { ...base, deterministic: true, feedUrl, name: feed.title, error: 'Key derivation failed — try a different passphrase.' };
    }
    secretKey = derived;
  } else {
    secretKey = generateSecretKey();
  }

  const pubkey = getPublicKey(secretKey);
  const npub = nip19.npubEncode(pubkey);
  const nsec = nip19.nsecEncode(secretKey);

  const now = Math.floor(Date.now() / 1000);
  const category = guessCategory(feed.title, feed.description);
  const hosts = (publisher || feed.author || '').trim();

  // 3a. Profile (kind 0) — gives the show a name + avatar in mentions,
  // profile links, and the directory's follower UI.
  const profileContent = JSON.stringify({
    name: feed.title || 'Podcast',
    display_name: feed.title || 'Podcast',
    about: feed.description
      ? feed.description.slice(0, 500)
      : 'A podcast on BCHNostr.',
    picture: feed.image || '',
    website: feed.link || '',
    // Marks the identity as a non-personal, bot-published show account.
    bot: true,
  });
  const profileEvent = finalizeEvent(
    { kind: 0, content: profileContent, tags: [], created_at: now },
    secretKey,
  );

  // 3b. The show event (kind 30078) — the directory entry itself, and the
  // only thing the app checks to treat the account as a podcast. A podcast
  // is fully standalone; it is never a professional profile (kind 30499).
  const { tags: showTags, content: showContent } = buildPodcastEvent({
    name: feed.title || 'Untitled podcast',
    description: feed.description,
    image: feed.image,
    feedUrl,
    website: feed.link,
    hosts: hosts ? [hosts] : [],
    category,
    guid: feed.guid || undefined,
  });
  const showEvent = finalizeEvent(
    { kind: PODCAST_KIND, content: showContent, tags: showTags, created_at: now },
    secretKey,
  );

  // 4. Publish. The show event is the one that must land; the kind 0
  // profile is a best-effort supporting event that gives the show a face.
  opts.onPhase?.('publishing');
  const relays = getRelays();
  await publishSigned(profileEvent, relays).catch(() => 0);
  const acceptedRelays = await publishSigned(showEvent, relays);

  // Warm the feed cache so the seeded show has episodes immediately.
  refreshFeed(feedUrl).catch(() => {});

  opts.onPhase?.('done');

  if (acceptedRelays === 0) {
    return {
      ...base, name: feed.title, feedUrl, episodes: feed.episodes.length,
      platform, npub, nsec, pubkey, updated: reusing, deterministic: usePassphrase,
      error: 'No relay accepted the show event. Try again.',
    };
  }

  return {
    input,
    status: 'ok',
    name: feed.title || 'Untitled podcast',
    feedUrl,
    episodes: feed.episodes.length,
    platform,
    npub,
    nsec,
    pubkey,
    updated: reusing,
    deterministic: usePassphrase,
    acceptedRelays,
    error: '',
  };
}

// ── Seed many, sequentially ───────────────────────────────────────
//
// One at a time on purpose: discovery hits a shared proxy and each show
// opens a burst of relay sockets. Sequential keeps us a well-behaved client
// and lets the UI show honest per-item progress.

export async function seedPodcasts(
  inputs: SeedInput[],
  onProgress?: (p: SeedProgress, result?: SeedResult) => void,
  opts: { passphrase?: string; operatorPubkey?: string } = {},
): Promise<SeedResult[]> {
  const results: SeedResult[] = [];
  for (let i = 0; i < inputs.length; i++) {
    const entry = inputs[i];
    onProgress?.({ index: i, total: inputs.length, input: entry.url, phase: 'resolving' });
    const result = await seedPodcast(entry, {
      onPhase: phase => onProgress?.({ index: i, total: inputs.length, input: entry.url, phase }),
      passphrase: opts.passphrase,
      operatorPubkey: opts.operatorPubkey,
    });
    results.push(result);
    onProgress?.({ index: i, total: inputs.length, input: entry.url, phase: 'done' }, result);
  }
  return results;
}
